import React, { useState, useEffect } from "react";
import { observable, action } from "mobx";
import { observer } from "mobx-react";
import AutoCompleteDropdown from "Components/common/AutoCompleteDropdown";
import { TagPopupButtons } from "Components/common/tags/TagPopupButtons";
import { accountsState } from "../AccountsState";
import {
  AccountListBody,
  CreateNewAccountList,
  CreateEditAccountListPopup
} from "../AccountListDropdown";
import "./organizeAccountsPopup.scss";

class OrganizeAccountsPopupState {
  @observable showPopup = false;
  @observable popupValues = {};

  @action
  setShowPopup = val => {
    this.showPopup = val;
    if (!val) {
      this.popupValues = {};
    }
  };

  @action
  setPopupValues = values => {
    this.popupValues = values;
  };
}

const organizeAccountsPopupState = new OrganizeAccountsPopupState();

// Mirrors Components/common/tags/TagPopup's "Organize Your Uploaded Contacts"
// step, but for the account CSV upload flow and with only the "Add to List"
// section — account tagging doesn't exist as a concept in this product yet.
const OrganizeAccountsPopup = observer(() => {
  const [selectedList, setSelectedList] = useState(null);
  const [showListDropdown, setShowListDropdown] = useState(false);
  const [showCreatePopup, setShowCreatePopup] = useState(false);

  const closeAction = () => {
    setSelectedList(null);
    setShowListDropdown(false);
    organizeAccountsPopupState.setShowPopup(false);
  };

  const confirmAction = () => {
    const { cbk } = organizeAccountsPopupState.popupValues;
    cbk && cbk(selectedList?.id || null);
    closeAction();
  };

  const accountListCbk = item => {
    setSelectedList(item);
    setShowListDropdown(false);
  };

  // Plain name only - used to match the active row in the open list
  // (AutoCompleteDropdown highlights by dropDownValue === item.name), so it
  // can't carry the extra " — N accounts" text that dropdownHeaderChildren
  // below renders instead.
  const selectedListLabel = selectedList?.name || "No list selected";

  // Replaces the dropdown trigger's default icon+name+arrow markup once a
  // list is picked, so the count can sit on its own on the right (number
  // highlighted, "accounts" stacked under it) instead of run into the name
  // as plain text. Falls back to the default rendering (via
  // AutoCompleteHeader's own React.isValidElement check) while nothing is
  // selected yet.
  const dropdownHeaderChildren = selectedList ? (
    <>
      <div className="titleWrapper">
        <i className="material-icons-outlined customListIcon">
          format_list_bulleted
        </i>
        <div className="title">{selectedList.name}</div>
      </div>
      <div className="organizeSelectedListCount">
        <span className="organizeSelectedListCountNumber">
          {selectedList.count || 0}
        </span>
        <span className="organizeSelectedListCountLabel">Accounts</span>
      </div>
      <span className={`arrow ${showListDropdown ? "activeArrow" : ""}`}>
        <i className="material-icons">expand_more</i>
      </span>
    </>
  ) : null;

  const handleListCreated = () => {
    // CreateEditAccountListPopup already pointed accountsState.activeAccountListId
    // at the new list and refreshed accountsState.accountLists before calling
    // onSaved - pick it up here instead of leaving the field blank and making
    // the user re-open the dropdown to select the list they just made.
    const newList = accountsState.accountLists.find(
      list => list.id === accountsState.activeAccountListId
    );
    if (newList) {
      setSelectedList(newList);
    }
  };

  useEffect(() => {
    if (organizeAccountsPopupState.showPopup) {
      setSelectedList(null);
      accountsState.fetchAccountLists();
    }
  }, [organizeAccountsPopupState.showPopup]);

  if (!organizeAccountsPopupState.showPopup) {
    return "";
  }

  return (
    <div className="popupWrapper tagPopup organizeAccountsPopup">
      {/* Hidden (not unmounted, so the picked list/dropdown state survives)
          while the create-list popup is open, so the two never show
          stacked/bleeding through each other - only one dialog is ever
          visible at a time. */}
      {!showCreatePopup && (
        <>
          <div className="confirmation-popup common-popup">
            <div className="popup-wrap animated customzoomIn" autoComplete="off">
              <div className="tagPopupTitle">Organize Your Uploaded Accounts</div>
              <p className="tagPopupSubTitle">
                Add them to a list to keep everything structured.
              </p>
              <div
                className="popup-close"
                title="Click here to close"
                onClick={closeAction}
              >
                <i className="material-icons">close</i>
              </div>
              <div className="bodyCont">
                <div className="addTagSelectBox">
                  <div className="tagPopupRow">
                    <p className="addContactToListTitle">Add to List</p>
                    <div className="inputWrapper">
                      <section className="customListWrapper">
                        <div className="customListContainer">
                          <AutoCompleteDropdown
                            optionList={accountsState.accountLists}
                            defaultValue={selectedListLabel}
                            cbk={accountListCbk}
                            loader={false}
                            showIcon={true}
                            icon={
                              <i className="material-icons-outlined customListIcon">
                                format_list_bulleted
                              </i>
                            }
                            updateListBody={true}
                            Body={AccountListBody}
                            showDropdown={showListDropdown}
                            setShowDropdown={setShowListDropdown}
                            hasAddNewList={true}
                            HeaderChildren={dropdownHeaderChildren}
                          >
                            <CreateNewAccountList
                              setShowDropdown={setShowListDropdown}
                              setShowCreatePopup={setShowCreatePopup}
                            />
                          </AutoCompleteDropdown>
                        </div>
                      </section>
                    </div>
                  </div>
                </div>
                <TagPopupButtons
                  btnAction={confirmAction}
                  backAction={closeAction}
                  nextBtnEnabled={true}
                  btnText="Upload Accounts"
                />
              </div>
            </div>
          </div>
          <div className="common-popup-mask"></div>
        </>
      )}
      {showCreatePopup && (
        <CreateEditAccountListPopup
          mode="create"
          onClose={() => setShowCreatePopup(false)}
          onSaved={handleListCreated}
        />
      )}
    </div>
  );
});

export default OrganizeAccountsPopup;
export { OrganizeAccountsPopup, organizeAccountsPopupState };
